import React, { useContext, useEffect, useRef, useState } from "react";
import cls from 'classnames';
import { Agreement } from "../../models/Agreement";
import { sleep } from "../../utils";
import { Busy } from "../core/busy";
import { AgreementList } from "./agreement_list";
import { fetchAgreements as fetchAgreementsService } from './service';
import styles from './archive.module.scss';
import { SalesContext } from "../../SalesContext";
import { Button } from "../core/button";
import FontAwesomeIcon from "../core/fa_icon";
import { TextInput } from "../core/text_input";

type Props = {
  onRevise?: (agreement: Agreement) => void;
}

/**
 * Lists recent agreements. Allows for revising an agreement.
 */
export const ArchiveView: React.FC<Props> = ({ onRevise }) => {
  const [agreements, setAgreements] = useState<Agreement[]>(null);
  const [fetchBusy, setFetchBusy] = useState(false);

  const [query, setQuery] = useState('');

  const [reloadCount, setReloadCount] = useState(0);
  const [fetchCount, setFetchCount] = useState(0);
  const [reloadEnabled, setReloadEnabled] = useState(true);

  const { setCreditCheckResult, setBundles, setAgreement: setCurrentAgreement } = useContext(SalesContext);

  const mounted = useRef(false);

  async function reload() {
    setReloadEnabled(false);
    setReloadCount(count => count + 1);
    fetchAgreements();

    //  Prevent spam clicking this button
    setTimeout(() => setReloadEnabled(true), 1500);
  }

  async function fetchAgreements() {
    if (fetchBusy) {
      return;
    }

    setFetchBusy(true);
    setFetchCount(count => count + 1);

    while (mounted.current) {
      try {
        var agreements = await fetchAgreementsService();
        break;
      } catch (e) {
        console.error(e);
        await sleep(3500);
      }
    }

    setAgreements(agreements);
    setFetchBusy(false);
  }

  useEffect(() => {
    mounted.current = true;
    fetchAgreements();

    document.body.style.overflow = 'hidden';

    return () => {
      mounted.current = false;
      document.body.style.overflow = null;
    }
  }, []);

  return (
    <div className={styles.view} data-busy={fetchBusy}>
      <header className={styles.header}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <h1>Arkiv {agreements?.length ? `(${agreements.length} avtal)` : null}</h1>
          <Button
            style={{ marginLeft: 6 }}
            weight="simple"
            title="Uppdatera arkivet"
            onClick={() => reload()}
            disabled={!reloadEnabled}>
            <FontAwesomeIcon className={reloadCount ? styles.spin : null} key={reloadCount} name="redo-alt" />
          </Button>
          <TextInput
            className={styles.query}
            placeholder="Sök kund, ID"
            onTextValueChange={setQuery}
            value={query}
            autoCorrect="false"
          />
        </div>
        <p>Senaste 7 dagarna</p>
      </header>
      {
        agreements ? (
          <AgreementList
            key={fetchCount}
            animate={fetchCount === 1}
            query={query}
            className={styles.list}
            agreements={agreements}
            onRevise={(agreement, meta) => {
              setCurrentAgreement(null);
              setCreditCheckResult(meta.creditCheckResult);
              setBundles(meta.bundles);
              onRevise?.(agreement);
            }}
            onDelete={agreement => {
              setAgreements(agreements => agreements.filter(otherAgreement => otherAgreement.key !== agreement.key));
              fetchAgreements()
            }}
            onCancel={agreement => fetchAgreements()}
            onOrderCreated={agreement => fetchAgreements()} />
        ) : null
      }
      {
        fetchBusy ? (
          <Busy className={styles.busy} />
        ) : null
      }
    </div>
  )
}